/**
 * Parse activity date string to preserve local date without timezone conversion.
 * This prevents issues where dates shift by a day due to timezone interpretation.
 * Uses only the date portion (YYYY-MM-DD) to determine day of week, ignoring time.
 *
 * @param {Object} activity - Activity object containing start_date_local or start_date
 * @returns {Date|null} - Parsed date at midnight local time, or null if invalid
 */
export const parseActivityDate = (activity) => {
  const dateString = activity?.start_date_local || activity?.start_date;
  if (!dateString) return null;

  // Extract just the date portion (YYYY-MM-DD) to avoid timezone issues with time
  // Format is typically: "2025-11-15T14:30:00Z" or "2025-11-15T14:30:00"
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    // Create date at midnight local time using only the date portion
    // This ensures the day of week matches the calendar date
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  // Fallback to standard parsing
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

/**
 * Parse a date string to a Date object.
 * Uses only the date portion to avoid timezone conversion issues.
 *
 * @param {string} dateString - ISO date string
 * @returns {Date|null} - Parsed date at midnight local time, or null if invalid
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;

  // Extract just the date portion (YYYY-MM-DD) to avoid timezone issues
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  // Fallback to standard parsing
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};
